import { baseApiAuthenticated } from '@/api/v2/index';
import { urlBuilder } from '@/api/utils';
import {
    BackendProductConfiguration,
    ProductConfiguration,
    transformProductConfigurationFromResponse,
    transformProductConfigurationToBackend,
} from '@/models/product';
import { convertIsoDateFromDate } from '@starjack/utils';

export const CACHE_KEY_BULK_UPDATES = 'PRODUCT_CONFIGURATIONS_BULK_UPDATES';

export type BulkTagMode = 'REPLACE' | 'ADD' | 'REMOVE';

type Configuration = {
    keycard: boolean;
    barcode: boolean;
    pickup: boolean;
    internalProduction: boolean;
    mobileFlow?: boolean;
};

const productConfigurationUrls = (liftOperatorId?: string) => ({
    productConfigurations: urlBuilder('liftoperators', 'productconfigurations'),
    bulkUpdates: urlBuilder(
        'liftoperators',
        `productconfigurations/${liftOperatorId}/bulk-update`
    ),
});

export const productConfigurationsApi = baseApiAuthenticated
    .enhanceEndpoints({
        addTagTypes: [
            'PRODUCT_CONFIGURATION',
            'CONDITION_LINKS',
            'IMPORTED_CONDITION_LINKS',
        ],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getProductConfigurations: builder.query<
                ProductConfiguration[],
                { liftOperatorId: string }
            >({
                query: ({ liftOperatorId }) =>
                    `${
                        productConfigurationUrls().productConfigurations
                    }/${liftOperatorId}`,

                transformResponse: (response: {
                    productConfigurations: BackendProductConfiguration[];
                }) => {
                    return response.productConfigurations.map(
                        transformProductConfigurationFromResponse
                    );
                },

                providesTags: ['PRODUCT_CONFIGURATION'],
            }),

            getProductConfiguration: builder.query<
                ProductConfiguration,
                { liftOperatorId: string; productId: string }
            >({
                query: ({ liftOperatorId }) =>
                    `${
                        productConfigurationUrls().productConfigurations
                    }/${liftOperatorId}`,

                transformResponse: (
                    response: {
                        productConfigurations: BackendProductConfiguration[];
                    },
                    _meta,
                    arg
                ) => {
                    return response.productConfigurations
                        .map(transformProductConfigurationFromResponse)
                        .find(
                            (productConfiguration) =>
                                productConfiguration.productId === arg.productId
                        );
                },

                providesTags: ['PRODUCT_CONFIGURATION'],
            }),
            postBulkUpdates: builder.mutation<
                boolean,
                {
                    liftOperatorId: string;
                    productIds: string[];
                    conditionLinkIds?: string[];
                    removeConditionLinks?: boolean;
                    validFrom: Date;
                    validTo: Date;
                    removeValidFromTo?: boolean;
                    removeStartDateFromTo?: boolean;
                    startDateValidFrom: Date;
                    startDateValidTo: Date;
                    enabled?: boolean;
                    portalConfiguration?: Configuration;
                    filterTagMode?: BulkTagMode;
                    filterTagIds?: string[];
                    tagMode?: BulkTagMode;
                    tagIds?: string[];
                }
            >({
                query: ({ liftOperatorId, ...rest }) => ({
                    url: productConfigurationUrls(liftOperatorId).bulkUpdates,
                    method: 'POST',
                    body: {
                        liftOperatorId,
                        ...rest,
                        validFrom: rest.validFrom
                            ? convertIsoDateFromDate(rest.validFrom)
                            : null,
                        validTo: rest.validTo
                            ? convertIsoDateFromDate(rest.validTo)
                            : null,
                        startDateValidFrom: rest.startDateValidFrom
                            ? convertIsoDateFromDate(rest.startDateValidFrom)
                            : null,
                        startDateValidTo: rest.startDateValidTo
                            ? convertIsoDateFromDate(rest.startDateValidTo)
                            : null,
                    },
                }),

                invalidatesTags: ['PRODUCT_CONFIGURATION'],
            }),

            putProductConfiguration: builder.mutation<
                ProductConfiguration,
                {
                    liftOperatorId: string;
                    productConfiguration: ProductConfiguration;
                }
            >({
                query: ({ liftOperatorId, productConfiguration }) => ({
                    url: `${
                        productConfigurationUrls().productConfigurations
                    }/${liftOperatorId}/${productConfiguration.id}`,
                    method: 'PUT',
                    body: transformProductConfigurationToBackend(
                        productConfiguration
                    ),
                }),

                transformResponse: (response: BackendProductConfiguration) => {
                    return transformProductConfigurationFromResponse(response);
                },

                invalidatesTags: [
                    'PRODUCT_CONFIGURATION',
                    'CONDITION_LINKS',
                    'IMPORTED_CONDITION_LINKS',
                ],
            }),

            postProductConfiguration: builder.mutation<
                ProductConfiguration,
                {
                    liftOperatorId: string;
                    productConfiguration: ProductConfiguration;
                }
            >({
                query: ({ liftOperatorId, productConfiguration }) => ({
                    url: `${
                        productConfigurationUrls().productConfigurations
                    }/${liftOperatorId}`,
                    method: 'POST',
                    body: transformProductConfigurationToBackend(
                        productConfiguration
                    ),
                }),

                transformResponse: (response: BackendProductConfiguration) => {
                    return transformProductConfigurationFromResponse(response);
                },

                invalidatesTags: ['PRODUCT_CONFIGURATION'],
            }),

            deleteProductConfiguration: builder.mutation<
                { success: true },
                {
                    liftOperatorId: string;
                    productConfiguration: ProductConfiguration;
                }
            >({
                query: ({ liftOperatorId, productConfiguration }) => ({
                    url: `${
                        productConfigurationUrls().productConfigurations
                    }/${liftOperatorId}/${productConfiguration.id}`,
                    method: 'DELETE',
                }),

                invalidatesTags: ['PRODUCT_CONFIGURATION'],
            }),
        }),
    });

export const {
    usePostBulkUpdatesMutation,
    useGetProductConfigurationsQuery,
    useGetProductConfigurationQuery,
    usePostProductConfigurationMutation,
    usePutProductConfigurationMutation,
    useDeleteProductConfigurationMutation,
} = productConfigurationsApi;
